import { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft, ChevronRight, CalendarDays, Truck, Package, ArrowUp, ArrowDown,
  Trash2, Pencil, Loader2, CheckCircle2, Route,
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Skeleton } from "../../components/ui/skeleton";
import { Modal, StatusBadge } from "./SharedComponents";
import { todayAsuncion, parseDateOnly } from "../../utils/format";
import { roleAllowed } from "../../utils/roles";

// BLOQUE 55 — Itinerario semanal de Retiro/Entrega. Vista calendario Dom→Sáb
// (según captura del cliente) en desktop, lista agrupada por día (con
// pestañas Dom..Sáb) en celular. Edición exclusiva del Jefe de Compras
// (+ Superadmin/Admin) — el resto ve en solo lectura (botones ausentes).
export function ItinerarioTab({ userRole, onOpenStop }) {
  const [weekDate, setWeekDate] = useState(todayAsuncion());
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileDayIdx, setMobileDayIdx] = useState(null);
  const [editStop, setEditStop] = useState(null);
  const [editSucursal, setEditSucursal] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [moveStop, setMoveStop] = useState(null);
  const [moveFecha, setMoveFecha] = useState("");
  const [busyId, setBusyId] = useState(null);

  const canEditLocal = roleAllowed(userRole, ["Superadministrador", "Administrador", "Jefe de Compras", "Compras"]);

  const fetchWeek = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/itinerary/week`, { params: { week_date: d }, withCredentials: true });
      setWeek(data);
      const todayIdx = data.days.findIndex(day => day.is_today);
      setMobileDayIdx(todayIdx >= 0 ? todayIdx : 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar el itinerario");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWeek(weekDate); }, [weekDate, fetchWeek]);

  const canEdit = week ? week.can_edit : canEditLocal;

  const addDays = (dateStr, days) => { const d = parseDateOnly(dateStr); d.setDate(d.getDate() + days); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

  const handleReorder = async (stop, direction) => {
    setBusyId(stop._id);
    try {
      await axios.put(`${API}/itinerary/${stop._id}/reorder`, { direction }, { withCredentials: true });
      fetchWeek(weekDate);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo reordenar"); }
    finally { setBusyId(null); }
  };

  const handleDelete = async (stop) => {
    if (!window.confirm(`¿Eliminar la parada #${stop.orden} (${stop.withdrawal_number})?`)) return;
    setBusyId(stop._id);
    try {
      await axios.delete(`${API}/itinerary/${stop._id}`, { withCredentials: true });
      toast.success("Parada eliminada");
      fetchWeek(weekDate);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
    finally { setBusyId(null); }
  };

  const openEdit = (stop) => { setEditStop(stop); setEditSucursal(stop.sucursal || ""); setEditNotes(stop.notes || ""); };
  const saveEdit = async () => {
    setBusyId(editStop._id);
    try {
      await axios.put(`${API}/itinerary/${editStop._id}`, { sucursal: editSucursal, notes: editNotes }, { withCredentials: true });
      toast.success("Parada actualizada");
      setEditStop(null);
      fetchWeek(weekDate);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo actualizar"); }
    finally { setBusyId(null); }
  };

  const openMove = (stop) => { setMoveStop(stop); setMoveFecha(stop.fecha); };
  const saveMove = async () => {
    if (!moveFecha || moveFecha === moveStop.fecha) { setMoveStop(null); return; }
    setBusyId(moveStop._id);
    try {
      await axios.put(`${API}/itinerary/${moveStop._id}/move`, { new_fecha: moveFecha }, { withCredentials: true });
      toast.success("Parada movida de día");
      setMoveStop(null);
      fetchWeek(weekDate);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo mover"); }
    finally { setBusyId(null); }
  };

  const StopCard = ({ stop }) => {
    const isRetiro = stop.tipo === "retiro";
    const clickable = stop.document_available && stop.withdrawal_number;
    return (
      <div
        className={`rounded-xl border p-3 transition-all ${stop.cumplido ? "bg-gray-50 border-gray-200 opacity-70" : isRetiro ? "bg-white border-sky-200" : "bg-white border-emerald-200"} ${clickable ? "cursor-pointer hover:shadow-sm" : ""}`}
        data-testid={`itinerario-stop-${stop._id}`}
        onClick={() => clickable && onOpenStop && onOpenStop(stop)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${stop.cumplido ? "bg-gray-300 text-white" : isRetiro ? "bg-sky-500 text-white" : "bg-emerald-500 text-white"}`} data-testid={`itinerario-orden-${stop._id}`}>
              {stop.orden}
            </span>
            {isRetiro ? <Truck size={13} className="text-sky-600 flex-shrink-0 mt-0.5" /> : <Package size={13} className="text-emerald-600 flex-shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <p className="text-xs font-bold text-gray-900 font-mono leading-tight break-all">{stop.withdrawal_number || "—"}</p>
              {!stop.document_available ? (
                <p className="text-[10px] text-red-500 font-semibold">Documento no disponible</p>
              ) : (
                <p className="text-[10px] text-gray-500 leading-snug">
                  {isRetiro ? (stop.provider_name || "—") : (stop.project_name || "—")}
                  {stop.sucursal && ` · ${stop.sucursal}`}
                </p>
              )}
            </div>
          </div>
          {stop.cumplido && <CheckCircle2 size={15} className="text-lime-500 flex-shrink-0" data-testid={`itinerario-cumplido-${stop._id}`} />}
        </div>
        {stop.document_available && (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <StatusBadge status={stop.status} />
            {stop.item_count > 0 && (
              <span className="text-[10px] text-gray-400">
                {stop.items_idx?.length ? `${stop.item_count} de esta parada` : `${stop.item_count} ${stop.item_count === 1 ? "ítem" : "ítems"}`}
              </span>
            )}
          </div>
        )}
        {/* Fix AH (2026-09-04): "al visualizar se muestra todos los ítems" (Sergio).
            La tarjeta lista los ítems marcados para ESTA parada, con nombre. */}
        {stop.document_available && stop.items_idx?.length > 0 && (
          <ul className="mt-1 space-y-0.5" data-testid={`itinerario-items-${stop._id}`}>
            {(stop.item_labels || []).slice(0, 3).map((label, i) => (
              <li key={i} className="text-[10px] text-gray-600 leading-snug truncate" title={label}>· {label}</li>
            ))}
            {(stop.item_labels || []).length > 3 && (
              <li className="text-[10px] text-gray-400">+{stop.item_labels.length - 3} más</li>
            )}
          </ul>
        )}
        {stop.notes && <p className="text-[10px] text-gray-400 mt-1 italic truncate">"{stop.notes}"</p>}
        {canEdit && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100" onClick={e => e.stopPropagation()}>
            <button disabled={stop.cumplido || busyId === stop._id} onClick={() => handleReorder(stop, "up")} className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30" data-testid={`itinerario-up-${stop._id}`} title="Subir"><ArrowUp size={12} /></button>
            <button disabled={stop.cumplido || busyId === stop._id} onClick={() => handleReorder(stop, "down")} className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30" data-testid={`itinerario-down-${stop._id}`} title="Bajar"><ArrowDown size={12} /></button>
            <button onClick={() => openMove(stop)} className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" data-testid={`itinerario-move-${stop._id}`} title="Mover de día"><CalendarDays size={12} /></button>
            <button onClick={() => openEdit(stop)} className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" data-testid={`itinerario-edit-${stop._id}`} title="Editar sucursal/obs."><Pencil size={12} /></button>
            <button onClick={() => handleDelete(stop)} className="p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 ml-auto" data-testid={`itinerario-delete-${stop._id}`} title="Eliminar parada"><Trash2 size={12} /></button>
          </div>
        )}
      </div>
    );
  };

  if (loading && !week) return <Skeleton className="h-64" />;
  if (!week) return null;

  return (
    <div className="space-y-4" data-testid="itinerario-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekDate(addDays(week.week_start, -7))} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" data-testid="itinerario-prev-week"><ChevronLeft size={14} /></button>
          <p className="text-sm font-semibold text-gray-700" data-testid="itinerario-week-range">
            {parseDateOnly(week.week_start).toLocaleDateString("es-PY", { day: "2-digit", month: "short" })} – {parseDateOnly(week.week_end).toLocaleDateString("es-PY", { day: "2-digit", month: "short", year: "numeric" })}
          </p>
          <button onClick={() => setWeekDate(addDays(week.week_start, 7))} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" data-testid="itinerario-next-week"><ChevronRight size={14} /></button>
          <button onClick={() => setWeekDate(todayAsuncion())} className="text-xs font-semibold text-[#2E6DA4] px-2.5 py-1.5 rounded-lg border border-[#2E6DA4]/30 hover:bg-blue-50" data-testid="itinerario-today-btn">Hoy</button>
        </div>
        {!canEdit && <span className="text-[11px] text-gray-400 flex items-center gap-1"><Route size={12} /> Solo lectura</span>}
      </div>

      {/* Desktop: grilla de 7 columnas */}
      <div className="hidden md:grid md:grid-cols-7 gap-2">
        {week.days.map((day, i) => (
          <div key={day.fecha} className={`rounded-xl border p-2 min-h-[140px] ${day.is_today ? "border-[#2E6DA4] bg-blue-50/40 ring-1 ring-[#2E6DA4]/30" : "border-gray-200 bg-gray-50/40"}`} data-testid={`itinerario-day-${day.fecha}`}>
            <p className={`text-[10px] font-bold uppercase tracking-wide mb-1.5 flex items-center justify-between ${day.is_today ? "text-[#2E6DA4]" : "text-gray-500"}`}>
              <span>{day.label}</span>
              <span>{parseDateOnly(day.fecha).getDate()}</span>
            </p>
            <div className="space-y-1.5">
              {day.stops.length === 0 ? (
                <p className="text-[10px] text-gray-300 italic">Sin paradas</p>
              ) : day.stops.map(stop => <StopCard key={stop._id} stop={stop} />)}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: pestañas por día + lista */}
      <div className="md:hidden space-y-3">
        <div className="flex gap-1 overflow-x-auto pb-1" data-testid="itinerario-mobile-day-tabs">
          {week.days.map((day, i) => (
            <button key={day.fecha} onClick={() => setMobileDayIdx(i)}
              className={`flex-shrink-0 px-3 py-2 rounded-lg text-[11px] font-semibold transition-all ${mobileDayIdx === i ? "bg-[#2E6DA4] text-white" : day.is_today ? "bg-blue-50 text-[#2E6DA4] border border-[#2E6DA4]/30" : "bg-gray-100 text-gray-500"}`}
              data-testid={`itinerario-mobile-day-btn-${day.fecha}`}
            >
              {day.label.slice(0, 3)} {parseDateOnly(day.fecha).getDate()}
              {day.stops.length > 0 && <span className="ml-1">({day.stops.length})</span>}
            </button>
          ))}
        </div>
        {mobileDayIdx != null && week.days[mobileDayIdx] && (
          <div className="space-y-2">
            {week.days[mobileDayIdx].stops.length === 0 ? (
              <p className="text-xs text-gray-400 italic text-center py-6">Sin paradas este día</p>
            ) : week.days[mobileDayIdx].stops.map(stop => <StopCard key={stop._id} stop={stop} />)}
          </div>
        )}
      </div>

      {/* Modal: editar sucursal/obs */}
      {editStop && (
        <Modal open={!!editStop} onClose={() => setEditStop(null)} title={`Editar parada · ${editStop.withdrawal_number}`}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sucursal</label>
              <input type="text" value={editSucursal} onChange={e => setEditSucursal(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="itinerario-edit-sucursal-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="itinerario-edit-notes-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditStop(null)} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={saveEdit} disabled={busyId === editStop._id} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="itinerario-save-edit-btn">
                {busyId === editStop._id ? <Loader2 size={15} className="animate-spin" /> : "Guardar"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: mover de día */}
      {moveStop && (
        <Modal open={!!moveStop} onClose={() => setMoveStop(null)} title={`Mover parada · ${moveStop.withdrawal_number}`}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nueva fecha</label>
              <input type="date" value={moveFecha} onChange={e => setMoveFecha(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="itinerario-move-date-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setMoveStop(null)} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={saveMove} disabled={busyId === moveStop._id} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="itinerario-save-move-btn">
                {busyId === moveStop._id ? <Loader2 size={15} className="animate-spin" /> : "Mover"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
